import {
  AlertTriangle,
  Check,
  Cpu,
  KeyRound,
  LoaderCircle,
  Plus,
  Power,
  RotateCcw,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  apiClient,
  type AiModel,
  type AiProvider,
  type AiProviderCredential,
  type AiProviderOption,
  type TaskDefault
} from "@/shared/api/api-client";
import { promptTaskLabel } from "@/shared/vocabulary/prompt-tasks";
import { formatShortDate } from "@/shared/utils/format";

import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { SearchSelect } from "@/shared/components/SearchSelect";
// Ponto de encaixe do Versa (14 em `versa/ARCHITECTURE.md`): a seção da
// IA própria dele em Provedores e chaves, que só existe com o Versa ligado.
import { VersaAiSection } from "@/features/versa/admin/VersaAiSection";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { Select } from "@/shared/components/Select";
/**
 * Administração → Modelos de IA.
 *
 * Antes desta tela, adicionar ou trocar a IA da plataforma exigia um
 * DEV: provedor e chaves viviam em variável de ambiente, e mexer nelas é
 * deploy. Aqui o Máster cadastra o que existe, com que chave, e
 * qual modelo cada tarefa usa por padrão.
 *
 * **A chave nunca volta do servidor** — só o mascarado. O campo de chave
 * nasce SEMPRE vazio, inclusive quando já há uma cadastrada: preenchê-lo
 * com a máscara faria um submit distraído gravar `••••3456` como chave.
 * Vazio significa "mantém a que está lá", que é o contrato do PUT.
 *
 * **A lista de provedores vem do servidor, e é a do litellm** (decisão
 * da dona, 2026-09-18). Até então eram três escritos aqui, e a tela
 * mostrava um cartão fixo pra cada. Com ~150 possíveis, a aba virou duas
 * seções: em cima o Gemini, que é a IA do site (o Copiloto só fala com
 * ele, e a mesma chave serve aos modelos Gemini de classificação);
 * embaixo, os provedores de CLASSIFICAÇÃO — só os que têm cadastro,
 * mais uma caixa com busca pra acrescentar qualquer um da lista. O que
 * cada provedor exige (endpoint, versão de API) também vem do servidor,
 * pra tela não voltar a ter uma lista sua.
 */

type Tab = "provedores" | "modelos" | "padroes";

/** O provedor do assistente do site. Espelha `COPILOTO_PROVIDER` do
 *  `web_api` — o Copiloto tem cliente próprio, só Gemini. */
const COPILOTO_PROVIDER = "gemini";

type Rascunho = { api_key: string; api_base: string; api_version: string };
const RASCUNHO_VAZIO: Rascunho = { api_key: "", api_base: "", api_version: "" };

function providerLabel(provedores: AiProviderOption[], id: string): string {
  return provedores.find(p => p.id === id)?.label ?? id;
}

/** Opções da caixa de escolha: os "mais usados" primeiro, como o
 *  servidor já manda, e o resto sob o título da lista inteira. */
function providerOptions(provedores: AiProviderOption[]) {
  return provedores.map(p => ({
    value: p.id,
    label: p.label,
    group: p.common ? "Mais usados" : "Todos os do litellm"
  }));
}

function ProvidersTab({ token, provedores }: { token: string; provedores: AiProviderOption[] }) {
  const [credenciais, setCredenciais] = useState<AiProviderCredential[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<AiProvider | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, Rascunho>>({});
  // Provedores escolhidos na caixa e ainda sem cadastro: viram cartão
  // vazio até a primeira gravação (aí passam a vir de `credenciais`).
  const [adicionados, setAdicionados] = useState<string[]>([]);

  const carregar = async () => {
    try {
      setCredenciais(await apiClient.listAiCredentials(token));
      setErro(null);
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const doProvedor = (provider: AiProvider) =>
    credenciais.find(linha => linha.provider === provider);

  const campo = (provider: AiProvider) => rascunho[provider] ?? RASCUNHO_VAZIO;

  const editar = (provider: AiProvider, parte: Partial<Rascunho>) =>
    setRascunho(anterior => ({ ...anterior, [provider]: { ...campo(provider), ...parte } }));

  const salvar = async (provider: AiProvider) => {
    const valores = campo(provider);
    const atual = doProvedor(provider);
    setSalvando(provider);
    setErro(null);
    try {
      await apiClient.saveAiCredential(token, provider, {
        // Chave em branco é OMITIDA do corpo — é assim que o servidor
        // entende "mantém a que está lá". Mandar string vazia seria
        // recusado (e com razão: campo limpo e salvo tem intenção).
        ...(valores.api_key.trim() ? { api_key: valores.api_key.trim() } : {}),
        api_base: valores.api_base.trim() || atual?.api_base || null,
        api_version: valores.api_version.trim() || atual?.api_version || null
      });
      setRascunho(anterior => ({ ...anterior, [provider]: RASCUNHO_VAZIO }));
      setAdicionados(anterior => anterior.filter(id => id !== provider));
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar a chave."));
    } finally {
      setSalvando(null);
    }
  };

  const desativar = async (provider: AiProvider) => {
    setSalvando(provider);
    setErro(null);
    try {
      await apiClient.deactivateAiCredential(token, provider);
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível desativar."));
    } finally {
      setSalvando(null);
    }
  };

  if (carregando) return <ApiState kind="loading">Carregando provedores…</ApiState>;

  // Cadastrados (ativos ou não — o desativado tem histórico e reativa
  // pela mesma tela), na ordem do servidor, e depois os recém-escolhidos.
  const cadastrados = credenciais
    .map(linha => linha.provider)
    .filter(provider => provider !== COPILOTO_PROVIDER);
  const listados = [...cadastrados, ...adicionados.filter(id => !cadastrados.includes(id))];
  const disponiveis = provedores.filter(
    p => p.id !== COPILOTO_PROVIDER && !listados.includes(p.id)
  );

  const cartao = (provider: AiProvider, extra?: { removivel?: boolean; nota?: ReactNode }) => (
    <ProviderCard
      key={provider}
      provider={provider}
      option={provedores.find(p => p.id === provider)}
      atual={doProvedor(provider)}
      valores={campo(provider)}
      salvando={salvando === provider}
      nota={extra?.nota}
      onChange={parte => editar(provider, parte)}
      onSave={() => void salvar(provider)}
      onDeactivate={() => void desativar(provider)}
      onRemove={
        extra?.removivel
          ? () => setAdicionados(anterior => anterior.filter(id => id !== provider))
          : undefined
      }
    />
  );

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}

      <section className="ai-admin-section" aria-labelledby="ai-section-copiloto">
        <h3 id="ai-section-copiloto">
          <Sparkles size={16} aria-hidden="true" /> Assistente do site (Mestra Help)
        </h3>
        <p>
          A Mestra Help fala só com o Gemini. A mesma chave serve aos modelos Gemini cadastrados
          para classificação; o modelo do chat se escolhe em{" "}
          <Link to="/admin/copiloto">Administração → Mestra Help</Link>.
        </p>
        {cartao(COPILOTO_PROVIDER)}
      </section>

      <VersaAiSection token={token} />

      <section className="ai-admin-section" aria-labelledby="ai-section-classificacao">
        <h3 id="ai-section-classificacao">
          <KeyRound size={16} aria-hidden="true" /> Provedores de classificação
        </h3>
        <p>
          Qualquer provedor que o litellm roteia. A chave é da instalação: as empresas escolhem
          o modelo, não o provedor.
        </p>
        <div className="ai-admin-add-provider">
          <SearchSelect
            label="Adicionar provedor"
            value=""
            allLabel="Escolher na lista…"
            options={providerOptions(disponiveis)}
            onChange={id => {
              if (id) setAdicionados(anterior => [...anterior, id]);
            }}
          />
        </div>
        {listados.length === 0 ? (
          <p className="copiloto-empty-list">
            Nenhum provedor de classificação cadastrado. Escolha um na lista acima.
          </p>
        ) : (
          listados.map(provider =>
            cartao(provider, { removivel: !cadastrados.includes(provider) })
          )
        )}
      </section>
    </div>
  );
}

/**
 * O cartão de UM provedor: chave, endpoint e versão da API, com o que é
 * obrigatório vindo do servidor (`requires_endpoint`,
 * `requires_api_version`). Os dois campos aparecem SEMPRE — desde
 * 2026-09-18 o servidor aceita endpoint em qualquer provedor (um
 * gateway compatível com a OpenAI é justamente `openai_like` mais um
 * endpoint) — e o botão só trava no que é exigido.
 *
 * `option` pode faltar: é uma credencial gravada com um id que a lista
 * do servidor não tem mais (litellm mudou). O cartão mostra o id e
 * deixa desativar; não inventa exigência nenhuma.
 */
function ProviderCard({
  provider,
  option,
  atual,
  valores,
  salvando,
  nota,
  onChange,
  onSave,
  onDeactivate,
  onRemove
}: {
  provider: AiProvider;
  option: AiProviderOption | undefined;
  atual: AiProviderCredential | undefined;
  valores: Rascunho;
  salvando: boolean;
  nota?: ReactNode;
  onChange: (parte: Partial<Rascunho>) => void;
  onSave: () => void;
  onDeactivate: () => void;
  onRemove?: () => void;
}) {
  const ativo = atual?.is_active ?? false;
  const exigeEndpoint = option?.requires_endpoint ?? false;
  const exigeVersao = option?.requires_api_version ?? false;
  const endpoint = valores.api_base.trim() || atual?.api_base || "";
  const versao = valores.api_version.trim() || atual?.api_version || "";
  const faltaObrigatorio = (exigeEndpoint && !endpoint) || (exigeVersao && !versao);

  const estado = ativo ? "is-active" : atual ? "is-inactive" : "is-empty";
  const autor = atual?.updated_by_name || atual?.updated_by;

  return (
    <div className={`card ai-provider-card ${estado}`}>
      <div className="ai-provider-head">
        <span className="ai-provider-icon" aria-hidden="true">
          <KeyRound size={18} />
        </span>
        <div className="ai-provider-title">
          <h3>{option?.label ?? provider}</h3>
          <p>
            {ativo ? (
              <>
                Chave <code className="ai-provider-hint">{atual?.api_key_hint}</code>
                {autor ? ` · cadastrada por ${autor}` : ""}
                {atual?.updated_at ? ` em ${formatShortDate(new Date(atual.updated_at))}` : ""}
              </>
            ) : atual ? (
              "Desativado — salve uma chave para reativar."
            ) : (
              "Nenhuma chave cadastrada."
            )}
          </p>
          {option && option.label !== option.id && (
            <span className="ai-admin-caption">id no litellm: {option.id}</span>
          )}
          {!option && (
            <span className="ai-admin-caption">
              Este provedor não está mais na lista do litellm desta versão.
            </span>
          )}
        </div>
        {ativo ? (
          <span className="badge-pill success">Ativo</span>
        ) : (
          <span className="badge-pill inactive">{atual ? "Inativo" : "Sem chave"}</span>
        )}
        {onRemove && (
          <button
            className="icon-button"
            type="button"
            aria-label={`Remover ${option?.label ?? provider} da lista`}
            onClick={onRemove}
          >
            <X size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {nota}

      {atual?.unreadable && (
        <p className="form-error" role="alert">
          <AlertTriangle size={14} aria-hidden="true" /> A chave gravada não pode ser
          lida com a chave-mestra deste ambiente. Digite-a novamente para regravá-la.
        </p>
      )}

      <div className="ai-provider-fields">
        <div className="admin-field-group ai-provider-key">
          <label className="field">
            <span>Chave de API</span>
            <input
              type="password"
              autoComplete="off"
              value={valores.api_key}
              placeholder={ativo ? "Deixe em branco para manter a atual" : "Cole a chave aqui"}
              onChange={event => onChange({ api_key: event.target.value })}
            />
          </label>
          <small className="admin-field-help">
            A chave é guardada cifrada e nunca volta para a tela — só os últimos dígitos.
          </small>
        </div>
        <label className="field">
          <span>Endpoint {exigeEndpoint ? "(obrigatório)" : "(opcional)"}</span>
          <input
            value={valores.api_base || atual?.api_base || ""}
            placeholder={exigeEndpoint ? "https://servidor.exemplo/v1" : "Só se o provedor pedir"}
            onChange={event => onChange({ api_base: event.target.value })}
          />
        </label>
        <label className="field">
          <span>Versão da API {exigeVersao ? "(obrigatório)" : "(opcional)"}</span>
          <input
            value={valores.api_version || atual?.api_version || ""}
            placeholder={exigeVersao ? "2024-06-01" : "Só se o provedor pedir"}
            onChange={event => onChange({ api_version: event.target.value })}
          />
        </label>
      </div>

      <div className="copiloto-admin-actions">
        {ativo && (
          <button
            className="secondary-button"
            type="button"
            disabled={salvando}
            onClick={onDeactivate}
          >
            <Power size={16} aria-hidden="true" /> Desativar
          </button>
        )}
        <button
          className="primary-button"
          type="button"
          disabled={salvando || (!ativo && !valores.api_key.trim()) || faltaObrigatorio}
          onClick={onSave}
        >
          {salvando ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
          Salvar
        </button>
      </div>
    </div>
  );
}

function ModelsTab({ token, provedores }: { token: string; provedores: AiProviderOption[] }) {
  const [modelos, setModelos] = useState<AiModel[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [novo, setNovo] = useState<{ provider: AiProvider; name: string; label: string }>({
    provider: "openai",
    name: "",
    label: ""
  });

  const carregar = async () => {
    try {
      setModelos(await apiClient.listAiModels(token, true));
      setErro(null);
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const cadastrar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      await apiClient.createAiModel(token, {
        provider: novo.provider,
        name: novo.name.trim(),
        label: novo.label.trim() || null
      });
      setNovo({ provider: novo.provider, name: "", label: "" });
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível cadastrar."));
    } finally {
      setSalvando(false);
    }
  };

  const alternar = async (modelo: AiModel) => {
    setErro(null);
    try {
      await apiClient.updateAiModel(token, modelo.id, { is_active: !modelo.is_active });
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível alterar."));
    }
  };

  const salvarPreco = async (
    modelo: AiModel,
    entrada: string | null,
    saida: string | null
  ) => {
    setErro(null);
    try {
      await apiClient.setAiModelPricing(token, modelo.id, {
        input_price_per_million: entrada,
        output_price_per_million: saida
      });
      await carregar();
      return true;
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar o preço."));
      return false;
    }
  };

  if (carregando) return <ApiState kind="loading">Carregando modelos…</ApiState>;

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}

      <div className="card">
        <div className="card-title">
          <div>
            <Plus size={18} aria-hidden="true" />
            <h3>Cadastrar modelo</h3>
            <p>
              O nome precisa ser exatamente o que o provedor espera — no Azure, o nome do
              deployment. Ele é único na instalação inteira.
            </p>
          </div>
        </div>
        <div className="ai-model-form">
          <SearchSelect
            label="Provedor"
            required
            value={novo.provider}
            valueLabel={providerLabel(provedores, novo.provider)}
            options={providerOptions(provedores)}
            onChange={provider => setNovo({ ...novo, provider })}
          />
          <label className="field">
            <span>Nome no provedor</span>
            <input
              value={novo.name}
              placeholder="gpt-4o"
              onChange={event => setNovo({ ...novo, name: event.target.value })}
            />
          </label>
          <label className="field">
            <span>Nome de exibição (opcional)</span>
            <input
              value={novo.label}
              placeholder="GPT-4o"
              onChange={event => setNovo({ ...novo, label: event.target.value })}
            />
          </label>
          <button
            className="primary-button"
            type="button"
            disabled={salvando || !novo.name.trim()}
            onClick={() => void cadastrar()}
          >
            {salvando ? <LoaderCircle className="spin" size={16} /> : <Plus size={16} />} Cadastrar
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-title">
          <div>
            <Cpu size={18} aria-hidden="true" />
            <h3>Modelos cadastrados</h3>
            <p>Modelo desativado some das listas de escolha, mas segue no histórico.</p>
          </div>
        </div>
        {modelos.length === 0 ? (
          <p className="copiloto-empty-list">Nenhum modelo cadastrado ainda.</p>
        ) : (
          <table className="ai-admin-table">
            <thead>
              <tr>
                <th>Modelo</th>
                <th>Provedor</th>
                <th>Preço (US$ por 1M tokens)</th>
                <th>Situação</th>
                <th aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {modelos.map(modelo => (
                <tr key={modelo.id} className={modelo.is_active ? undefined : "row-inactive"}>
                  <td>
                    <strong>{modelo.name}</strong>
                    {modelo.label && <span className="ai-admin-caption"> {modelo.label}</span>}
                  </td>
                  <td>{providerLabel(provedores, modelo.provider)}</td>
                  <td>
                    <PricingCell modelo={modelo} onSave={salvarPreco} />
                  </td>
                  <td>
                    <span className={`badge-pill ${modelo.is_active ? "success" : "inactive"}`}>
                      {modelo.is_active ? "Ativo" : "Desativado"}
                    </span>
                  </td>
                  <td className="ai-admin-row-actions">
                    <button
                      className="text-button"
                      type="button"
                      onClick={() => void alternar(modelo)}
                    >
                      {modelo.is_active ? (
                        <>
                          <Trash2 size={14} aria-hidden="true" /> Desativar
                        </>
                      ) : (
                        <>
                          <RotateCcw size={14} aria-hidden="true" /> Reativar
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/**
 * A célula de preço: mostra os dois valores, e vira um formulário de
 * duas caixas ao clicar em Editar.
 *
 * Caixa de TEXTO com `inputMode="decimal"`, não `type="number"`:
 * precedente `CenaAdmin`. Vazia é "sem preço" e `0` é "de graça" — a
 * diferença é visível no texto, então não precisa do rádio que o
 * `fav_neutral` precisou (lá era 1 campo entre 25 numa grade densa).
 *
 * **Salvar zero pede confirmação.** Cada linha de consumo FOTOGRAFA o
 * preço na hora da chamada (decisão da dona, 2026-09-11), então um `0`
 * digitado por engano vira custo zero definitivo nas chamadas daquela
 * janela — não há como corrigir depois.
 */
/** Só pra LEITURA: `"2.500000"` vira `"2.5"`. A caixa de edição recebe
 *  a string crua do servidor, porque é ela que volta na comparação. */
function trimPrice(value: string): string {
  return value.includes(".") ? value.replace(/\.?0+$/, "") : value;
}

function PricingCell({
  modelo,
  onSave
}: {
  modelo: AiModel;
  onSave: (modelo: AiModel, entrada: string | null, saida: string | null) => Promise<boolean>;
}) {
  const [editando, setEditando] = useState(false);
  const [entrada, setEntrada] = useState(modelo.input_price_per_million ?? "");
  const [saida, setSaida] = useState(modelo.output_price_per_million ?? "");
  const [salvando, setSalvando] = useState(false);

  const abrir = () => {
    setEntrada(modelo.input_price_per_million ?? "");
    setSaida(modelo.output_price_per_million ?? "");
    setEditando(true);
  };

  const salvar = async () => {
    const e = entrada.trim().replace(",", ".");
    const s = saida.trim().replace(",", ".");
    const ambosVazios = e === "" && s === "";
    if (!ambosVazios && (e === "" || s === "")) {
      // O servidor também recusa (422); avisar aqui poupa a ida.
      window.alert("Informe o preço de entrada E o de saída, ou deixe os dois vazios.");
      return;
    }
    const zerou = !ambosVazios && (Number(e) === 0 || Number(s) === 0);
    if (
      zerou &&
      !window.confirm(
        "Preço zero significa 'de graça'. As chamadas feitas enquanto ele valer " +
          "ficam com custo zero para sempre — isso não pode ser corrigido depois. Salvar?"
      )
    ) {
      return;
    }
    setSalvando(true);
    const ok = await onSave(modelo, ambosVazios ? null : e, ambosVazios ? null : s);
    setSalvando(false);
    if (ok) setEditando(false);
  };

  if (!editando) {
    return (
      <span className="ai-model-pricing">
        {modelo.input_price_per_million === null ? (
          <span
            className="ai-admin-caption"
            title="Sem preço cadastrado — o consumo deste modelo fica sem custo apurado"
          >
            sem preço
          </span>
        ) : (
          <span className="ai-model-pricing-value">
            {trimPrice(modelo.input_price_per_million)} /{" "}
            {trimPrice(modelo.output_price_per_million ?? "")}
          </span>
        )}
        <button type="button" className="link-button" onClick={abrir}>
          Editar
        </button>
      </span>
    );
  }

  return (
    <span className="ai-model-pricing is-editing">
      <input
        type="text"
        inputMode="decimal"
        aria-label={`Preço de entrada de ${modelo.name}, em dólar por milhão de tokens`}
        placeholder="entrada"
        value={entrada}
        onChange={event => setEntrada(event.target.value)}
        disabled={salvando}
      />
      <input
        type="text"
        inputMode="decimal"
        aria-label={`Preço de saída de ${modelo.name}, em dólar por milhão de tokens`}
        placeholder="saída"
        value={saida}
        onChange={event => setSaida(event.target.value)}
        disabled={salvando}
      />
      <button type="button" className="primary-button" onClick={() => void salvar()} disabled={salvando}>
        Salvar
      </button>
      <button type="button" className="link-button" onClick={() => setEditando(false)} disabled={salvando}>
        Cancelar
      </button>
    </span>
  );
}

function DefaultsTab({ token }: { token: string }) {
  const [padroes, setPadroes] = useState<TaskDefault[]>([]);
  const [opcoes, setOpcoes] = useState<AiModel[]>([]);
  const [tarefas, setTarefas] = useState<string[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState<string | null>(null);

  const carregar = async () => {
    try {
      // A lista de tarefas vem junto dos padrões, e não de
      // `/prompt-models`: aquela rota é escopada por empresa, e esta
      // tela é de topo — sem empresa ativa ela devolveria 401 e a
      // tabela nasceria vazia, sem nada explicando.
      const [lista, modelos] = await Promise.all([
        apiClient.listAiTaskDefaults(token),
        apiClient.listAiModels(token)
      ]);
      setPadroes(lista);
      setOpcoes(modelos.filter(modelo => modelo.is_active));
      setTarefas(lista.map(linha => linha.task));
      setErro(null);
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível carregar."));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    void carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const escolher = async (task: string, modelId: string) => {
    setSalvando(task);
    setErro(null);
    try {
      if (modelId) await apiClient.setAiTaskDefault(token, task, Number(modelId));
      else await apiClient.clearAiTaskDefault(token, task);
      await carregar();
    } catch (error) {
      setErro(errorMessage(error, "Não foi possível salvar o padrão."));
    } finally {
      setSalvando(null);
    }
  };

  if (carregando) return <ApiState kind="loading">Carregando padrões…</ApiState>;

  const doTask = (task: string) => padroes.find(linha => linha.task === task);

  return (
    <div className="ai-admin-stack">
      {erro && <p className="form-error" role="alert">{erro}</p>}
      <div className="card">
        <div className="card-title">
          <div>
            <Cpu size={18} aria-hidden="true" />
            <h3>Padrão da instalação</h3>
            <p>
              O modelo que cada tarefa usa nas empresas que não escolheram outro. Tarefa sem
              padrão e sem escolha própria não classifica.
            </p>
          </div>
        </div>
        {opcoes.length === 0 ? (
          <p className="copiloto-empty-list">
            Cadastre um modelo e a chave do provedor dele antes de definir padrões.
          </p>
        ) : (
          <table className="ai-admin-table">
            <thead>
              <tr>
                <th>Tarefa</th>
                <th>Modelo padrão</th>
                <th className="ai-default-state-col">Situação</th>
              </tr>
            </thead>
            <tbody>
              {tarefas.map(task => {
                const atual = doTask(task);
                return (
                  <tr key={task} className={atual ? undefined : "row-inactive"}>
                    <td>{promptTaskLabel(task)}</td>
                    <td>
                      <Select
                        value={atual?.model_id === null || atual?.model_id === undefined ? "" : String(atual.model_id)}
                        disabled={salvando === task}
                        onChange={event => void escolher(task, event.target.value)}
                      >
                        <option value="">Sem padrão — não classifica</option>
                        {/* O padrão gravado que saiu do catálogo ativo entra
                            como opção órfã, senão o seletor mostraria "sem
                            padrão" numa tarefa que tem um. */}
                        {atual?.model_id !== null &&
                          atual?.model_id !== undefined &&
                          !opcoes.some(o => o.id === atual.model_id) && (
                            <option value={String(atual.model_id)}>
                              {atual.model_name ?? `#${atual.model_id}`} (desativado)
                            </option>
                          )}
                        {opcoes.map(modelo => (
                          <option key={modelo.id} value={String(modelo.id)}>
                            {modelo.label ? `${modelo.label} (${modelo.name})` : modelo.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="ai-default-state-col">
                      {salvando === task ? (
                        <LoaderCircle className="spin" size={14} aria-label="Salvando" />
                      ) : atual?.model_id != null ? (
                        <span className="badge-pill success">Classifica</span>
                      ) : (
                        <span className="badge-pill warning">Não classifica</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function AiModelsAdmin({ token }: { token: string }) {
  const [tab, setTab] = useState<Tab>("provedores");
  // A lista do litellm, carregada uma vez pra página: as duas primeiras
  // abas a usam (cadastrar chave, escolher o provedor de um modelo).
  const [provedores, setProvedores] = useState<AiProviderOption[] | null>(null);
  const [erroProvedores, setErroProvedores] = useState<string | null>(null);

  useEffect(() => {
    let carga = true;
    apiClient
      .listAiProviders(token)
      .then(lista => {
        if (carga) setProvedores(lista);
      })
      .catch((error: unknown) => {
        if (carga) setErroProvedores(errorMessage(error, "Não foi possível carregar a lista de provedores."));
      });
    return () => {
      carga = false;
    };
  }, [token]);

  return (
    <div className="copiloto-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Modelos de IA"
        description="As IAs que a plataforma pode usar, suas chaves e o modelo padrão de cada tarefa de classificação."
      />

      <nav className="copiloto-admin-tabs" aria-label="Seções de modelos de IA">
        <button
          className={tab === "provedores" ? "active" : undefined}
          type="button"
          onClick={() => setTab("provedores")}
        >
          Provedores e chaves
        </button>
        <button
          className={tab === "modelos" ? "active" : undefined}
          type="button"
          onClick={() => setTab("modelos")}
        >
          Modelos
        </button>
        <button
          className={tab === "padroes" ? "active" : undefined}
          type="button"
          onClick={() => setTab("padroes")}
        >
          Padrão por tarefa
        </button>
      </nav>

      {erroProvedores ? (
        <ApiState kind="error">{erroProvedores}</ApiState>
      ) : provedores === null ? (
        <ApiState kind="loading">Carregando a lista de provedores…</ApiState>
      ) : (
        <>
          {tab === "provedores" && <ProvidersTab token={token} provedores={provedores} />}
          {tab === "modelos" && <ModelsTab token={token} provedores={provedores} />}
        </>
      )}
      {tab === "padroes" && <DefaultsTab token={token} />}
    </div>
  );
}
